/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import {
  SortableContainer,
  SortableContainerProps,
  SortableElement,
  SortableElementProps,
} from "react-sortable-hoc"
import {
  leftScrollBtn,
  photoList,
  rightScrollBtn,
  scrollContainer,
} from "components/drawers/styles"
import { ItemDetailPhoto } from "components/drawers"
import { css, jsx } from "@emotion/core"
import { useEffect, useRef, useState } from "react"
import { Button } from "@duxton-web/ui"
import { ChevronLeftUniversal, ChevronRightUniversal } from "@duxton-web/icons"
import arrayMove from "array-move"

interface SortablePhotoProps extends SortableElementProps {
  image: string
  total: number
  disabledAction?: boolean

  handleEdit(value: string): void

  handleDelete(value: string): void
}

const SortablePhoto = SortableElement(
  ({
    image,
    total,
    handleEdit,
    handleDelete,
    disabledAction,
  }: SortablePhotoProps) => {
    return (
      <ItemDetailPhoto
        data-testid="sortable-photo"
        css={css`
          margin-bottom: 16px;
          z-index: 9999;
        `}
        src={image}
        onClickEdit={() => {
          handleEdit(image)
        }}
        onClickAdd={() => {}}
        onClickDelete={() => {
          handleDelete(image)
        }}
        disabled={!!disabledAction}
        showDragger={total > 1}
      />
    )
  },
)

interface SortablePhotoListProps extends SortableContainerProps {
  photos?: string[]
  multiPicsLimit: number
  disabledAction?: boolean

  handleEditElement(value: string): void

  handleDeleteElement(value: string): void

  handleAddElement(value: string): void
}

export const SortablePhotoList = SortableContainer(
  ({
    photos,
    handleEditElement,
    handleDeleteElement,
    multiPicsLimit,
    handleAddElement,
    disabledAction,
  }: SortablePhotoListProps) => {
    return (
      <div css={photoList}>
        {photos &&
          photos.map((value, index) => (
            <SortablePhoto
              // eslint-disable-next-line react/no-array-index-key
              key={`photo-${index}`}
              image={value}
              index={index}
              total={photos.length}
              handleDelete={handleDeleteElement}
              handleEdit={handleEditElement}
              disabledAction={disabledAction}
            />
          ))}
        {photos && photos.length < multiPicsLimit && (
          <ItemDetailPhoto
            data-testid="item-detail-photo"
            css={css`
              margin-bottom: 16px;
            `}
            src=""
            onClickEdit={() => {}}
            onClickAdd={() => {
              handleAddElement("")
            }}
            onClickDelete={() => {}}
            disabled={!!disabledAction}
          />
        )}
      </div>
    )
  },
)

export interface ItemMultiPicsProps {
  images: string[]
  multiPicsLimit: number
  disabled?: boolean

  handleEditElement(value: string): void

  handleAddElement(value: string): void

  onChange(value: string[]): void

  multiPicEditingImgIndex: number
  multiPicEditingImg: string
}

const ItemMultiPics = ({
  images,
  multiPicsLimit,
  handleEditElement,
  handleAddElement,
  onChange,
  multiPicEditingImgIndex,
  multiPicEditingImg,
  disabled,
}: ItemMultiPicsProps) => {
  const [showLeftBtn, setShowLeftBtn] = useState(false)
  const [showRightBtn, setShowRightBtn] = useState(false)

  const scrollStep = 400
  const scrl = useRef<any>(null)

  // const [editingIndex, setEditingIndex] = useState<number>(-1)
  const onPicsSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number
    newIndex: number
  }) => {
    const newImages = arrayMove(images, oldIndex, newIndex)
    onChange(newImages)
  }

  const deleteFromImages = (image: string) => {
    const newImages = [...images]
    const index = newImages.indexOf(image)
    newImages.splice(index, 1)
    onChange(newImages)
  }

  const handleScroll = () => {
    // @ts-ignore
    const scrollWidth = scrl.current.scrollContainer.scrollWidth

    // @ts-ignore
    const offsetWidth = scrl.current.scrollContainer.offsetWidth

    // @ts-ignore
    const scrollLeft = scrl.current.scrollContainer.scrollLeft

    if (scrollLeft > 0) {
      setShowLeftBtn(true)
    } else {
      setShowLeftBtn(false)
    }
    if (scrollLeft + offsetWidth < scrollWidth) {
      setShowRightBtn(true)
    } else {
      setShowRightBtn(false)
    }
  }

  const onClickScrollLeft = () => {
    // @ts-ignore
    scrl.current.scrollContainer.scrollLeft -= scrollStep
    handleScroll()
  }

  const onClickScrollRight = () => {
    // @ts-ignore
    scrl.current.scrollContainer.scrollLeft += scrollStep
    handleScroll()
  }

  useEffect(() => {
    if (images.length > 2) {
      setShowRightBtn(true)
    }
  }, [images])

  useEffect(() => {
    if (multiPicEditingImg === "") {
      return
    }
    if (multiPicEditingImgIndex !== -1) {
      if (images[multiPicEditingImgIndex] !== multiPicEditingImg) {
        // replace
        const newImages = [...images]
        newImages[multiPicEditingImgIndex] = multiPicEditingImg
        onChange(newImages)
      }
    } else {
      // append
      const newImages = [...images, multiPicEditingImg]
      onChange(newImages)
    }
  }, [multiPicEditingImg])

  return (
    <div css={scrollContainer} onScroll={handleScroll}>
      <Button
        data-testid="left-button"
        css={leftScrollBtn}
        type="primary"
        shape="circle"
        onClick={onClickScrollLeft}
        hidden={!(images.length > 2 && showLeftBtn)}
      >
        <ChevronLeftUniversal style={{ fontSize: "16px" }} />
      </Button>
      <SortablePhotoList
        data-testid="photo-list"
        onSortEnd={onPicsSortEnd}
        photos={images}
        multiPicsLimit={multiPicsLimit}
        lockAxis="x"
        axis="x"
        useDragHandle
        lockToContainerEdges
        ref={scrl}
        handleEditElement={handleEditElement}
        handleAddElement={handleAddElement}
        handleDeleteElement={deleteFromImages}
        disabledAction={disabled}
      />
      <Button
        data-testid="right-button"
        css={rightScrollBtn}
        type="primary"
        shape="circle"
        onClick={onClickScrollRight}
        hidden={!(images.length > 2 && showRightBtn)}
      >
        <ChevronRightUniversal style={{ fontSize: "16px" }} />
      </Button>
    </div>
  )
}
export default ItemMultiPics
